import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft, 
  Package, 
  Clock, 
  TrendingUp, 
  AlertTriangle,
  Camera as CameraIcon,
  MessageSquare,
  Image as ImageIcon,
  Activity,
  PackagePlus,
  RotateCcw,
  Trash2,
  Eye,
  Play,
  CheckSquare,
  Cloud
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import LogPickModal from "@/components/LogPickModal";
import LogReturnModal from "@/components/LogReturnModal";
import LogWasteModal from "@/components/LogWasteModal";
import PickLogModal from "@/components/PickLogModal";
import PalletLog from "@/components/PalletLog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { API } from "@/config";

// Helper functions
const formatBOMQuantity = (qty, unit, itemType) => {
  return `${qty.toFixed(2)} ${unit}`;
};

const calculateActualUsage = (lots) => {
  if (!lots || lots.length === 0) return 0;
  
  const totalPicks = lots.reduce((sum, lot) => {
    const picks = lot.picks || [];
    return sum + picks.reduce((pickSum, pick) => pickSum + pick.picked_qty, 0);
  }, 0);
  
  const totalReturns = lots.reduce((sum, lot) => {
    const returns = lot.returns || [];
    return sum + returns.reduce((retSum, ret) => retSum + ret.returned_qty, 0);
  }, 0);
  
  return totalPicks - totalReturns;
};

const calculateTheoreticalUsage = (plannedQty, actualProduced, totalPlanned) => {
  if (!actualProduced || actualProduced === 0) return 0;
  return (plannedQty / totalPlanned) * actualProduced;
};

const calculateYield = (actual, theoretical) => {
  if (!actual || actual === 0) return 0;
  if (!theoretical || theoretical === 0) return 0;
  return ((theoretical / actual) * 100).toFixed(2);
};

const getYieldColor = (yieldPct) => {
  if (yieldPct === 0 || yieldPct === '0.00') return 'text-gray-400';
  if (yieldPct > 97.5) return 'text-green-600';
  if (yieldPct >= 90) return 'text-orange-500';
  return 'text-red-600';
};

const WorkOrderDetailPage = () => {
  const { wo_id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPickModal, setShowPickModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showWasteModal, setShowWasteModal] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [showPickLogModal, setShowPickLogModal] = useState(false);
  const [pickLogComponent, setPickLogComponent] = useState(null);
  const [showQADialog, setShowQADialog] = useState(false);
  const [selectedQAInspection, setSelectedQAInspection] = useState(null);
  const [qaStatus, setQAStatus] = useState('');
  const [qaResultNotes, setQAResultNotes] = useState('');
  const [chartTimeRange, setChartTimeRange] = useState('last1hour'); // today, last4hours, last1hour
  
  const from = searchParams.get('from') || 'schedule';
  const backPath = from === 'dashboard' ? '/dashboard' : '/schedule';
  const backLabel = from === 'dashboard' ? 'Back to Production Dashboard' : 'Back to Schedule';

  // Compute production start time from activity log or actual start fields
  const getProductionStartTime = (wo) => {
    if (!wo) return null;
    const activityLog = wo.activity_log || [];

    // Prefer explicit production started activity
    const startedActivities = activityLog.filter(
      (a) =>
        (a.activity_type === 'status_change' || a.action === 'production_started' || a.activity_type === 'production_started') &&
        (a.description || '').toLowerCase().includes('production started')
    );

    if (startedActivities.length > 0) {
      const earliest = startedActivities.reduce((acc, cur) => {
        if (!cur.timestamp) return acc;
        if (!acc) return cur;
        return cur.timestamp < acc.timestamp ? cur : acc;
      }, null);
      if (earliest?.timestamp) {
        return new Date(earliest.timestamp);
      }
    }

    // Fallback to actual_date + actual_start_time if available
    if (wo.actual_date && wo.actual_start_time) {
      return new Date(`${wo.actual_date}T${wo.actual_start_time}:00`);
    }

    // Last resort: first pallet completion
    const palletActivities = (activityLog || []).filter(
      (a) => a.activity_type === 'pallet_complete' || a.action === 'pallet_complete'
    );
    if (palletActivities.length > 0) {
      const earliestPallet = palletActivities.reduce((acc, cur) => {
        if (!cur.timestamp) return acc;
        if (!acc) return cur;
        return cur.timestamp < acc.timestamp ? cur : acc;
      }, null);
      if (earliestPallet?.timestamp) {
        return new Date(earliestPallet.timestamp);
      }
    }

    return null;
  };

  // Extract pallet completion events (cases per pallet + timestamp)
  const getPalletEvents = (wo) => {
    if (!wo) return [];
    const activityLog = wo.activity_log || [];

    const palletActivities = activityLog.filter(
      (entry) => entry.activity_type === 'pallet_complete' || entry.action === 'pallet_complete'
    );

    const events = palletActivities
      .map((entry) => {
        if (!entry.timestamp) return null;
        const timestamp = new Date(entry.timestamp);
        // Parse "{n} cases" from description
        let cases = 0;
        if (entry.description) {
          const match = entry.description.match(/(\d+(?:\.\d+)?)\s*cases/i);
          if (match) {
            const parsed = parseFloat(match[1]);
            if (!Number.isNaN(parsed)) {
              cases = parsed;
            }
          }
        }
        return { timestamp, cases, entry };
      })
      .filter(Boolean);

    events.sort((a, b) => a.timestamp - b.timestamp);
    return events;
  };

  const getTargetUPM = (wo) => {
    if (!wo || !wo.planned_start_time || !wo.planned_end_time || !wo.planned_quantity) return null;
    const [startH, startM] = wo.planned_start_time.split(':').map(Number);
    const [endH, endM] = wo.planned_end_time.split(':').map(Number);
    const plannedMinutes = endH * 60 + endM - (startH * 60 + startM);
    if (plannedMinutes <= 0) return null;
    const unitsPerCase = wo.units_per_case || 1;
    return (wo.planned_quantity * unitsPerCase) / plannedMinutes;
  };

  // UPM chart based on pallet completion log
  const generateUPMData = (wo, timeRange) => {
    if (!wo) return [];
    const startTime = getProductionStartTime(wo);
    if (!startTime) return [];

    const events = getPalletEvents(wo);
    if (!events.length) return [];

    const targetUPM = getTargetUPM(wo);
    const unitsPerCase = wo.units_per_case || 1;
    const now = new Date();

    let windowMinutes = null;
    if (timeRange === 'last4hours') windowMinutes = 4 * 60;
    else if (timeRange === 'last1hour') windowMinutes = 60;
    // 'today' shows all pallet events

    let cumulativeUnits = 0;

    return events.reduce((acc, { timestamp, cases }) => {
      if (windowMinutes !== null) {
        const diffFromNow = (now - timestamp) / 60000;
        if (diffFromNow > windowMinutes) {
          return acc;
        }
      }

      const elapsedMinutes = (timestamp - startTime) / 60000;
      if (elapsedMinutes <= 0) {
        return acc;
      }

      cumulativeUnits += cases * unitsPerCase;
      const upm = cumulativeUnits / elapsedMinutes;

      const timeLabel = timestamp.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });

      const variancePct = targetUPM
        ? ((upm - targetUPM) / targetUPM) * 100
        : 0;

      acc.push({
        time: timeLabel,
        upm: parseFloat(upm.toFixed(1)),
        target: targetUPM || 0,
        variance: parseFloat(variancePct.toFixed(1)),
        elapsedMinutes,
        cumulativeUnits
      });

      return acc;
    }, []);
  };

  // Hourly production chart based on pallet completion log
  const generateHourlyData = (wo, timeRange) => {
    if (!wo) return [];
    const events = getPalletEvents(wo);
    if (!events.length) return [];

    const unitsPerCase = wo.units_per_case || 1;
    const targetUPM = getTargetUPM(wo);
    const targetUnitsPerHour = targetUPM ? targetUPM * 60 : null;
    const now = new Date();

    let windowHours = null;
    if (timeRange === 'last4hours') windowHours = 4;
    else if (timeRange === 'last1hour') windowHours = 1;
    // 'today' shows all pallet events

    const buckets = new Map(); // key: ISO hour, value: units in that hour

    events.forEach(({ timestamp, cases }) => {
      if (windowHours !== null) {
        const diffHours = (now - timestamp) / 3600000;
        if (diffHours > windowHours) {
          return;
        }
      }

      const bucketTime = new Date(timestamp);
      bucketTime.setMinutes(0, 0, 0);
      const key = bucketTime.toISOString();
      const currentUnits = buckets.get(key) || 0;
      buckets.set(key, currentUnits + cases * unitsPerCase);
    });

    const sortedBuckets = Array.from(buckets.entries())
      .map(([iso, units]) => ({ time: new Date(iso), units }))
      .sort((a, b) => a.time - b.time);

    return sortedBuckets.map(({ time, units }) => {
      const hourLabel = time.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
      const actual = units;
      const target = targetUnitsPerHour || 0;
      const variancePct = target
        ? ((actual - target) / target) * 100
        : 0;

      return {
        hour: hourLabel,
        actual,
        target,
        variance: parseFloat(variancePct.toFixed(1))
      };
    });
  };

  useEffect(() => {
    const fetchWorkOrder = async () => {
      try {
        const response = await axios.get(`${API}/work-orders/${wo_id}`);
        setWorkOrder(response.data);
      } catch (error) {
        console.error("Error fetching work order:", error);
        toast.error("Failed to load work order details");
      } finally {
        setLoading(false);
      }
    };

    fetchWorkOrder();
    
    // Set up polling for real-time updates
    const interval = setInterval(fetchWorkOrder, 5000);
    return () => clearInterval(interval);
  }, [wo_id]);

  const handleRefreshBOM = async () => {
    if (!wo_id) return;
    try {
      const response = await axios.put(`${API}/work-orders/${wo_id}/refresh-bom`);
      if (response.data?.work_order) setWorkOrder(response.data.work_order);
      toast.success("BOM applied to work order. Pick/Return buttons are now available.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to refresh BOM. Add a BOM for this item first (Item detail → BOM section).");
    }
  };

  const handleOpenPickModal = (component) => {
    setSelectedComponent(component);
    setShowPickModal(true);
  };

  const handleOpenReturnModal = (component) => {
    setSelectedComponent(component);
    setShowReturnModal(true);
  };

  const handleOpenWasteModal = (component) => {
    setSelectedComponent(component);
    setShowWasteModal(true);
  };

  const handleViewPickLog = (component) => {
    setPickLogComponent(component);
    setShowPickLogModal(true);
  };

  const handleOpenQADialog = (inspection) => {
    setSelectedQAInspection(inspection);
    setQAStatus(inspection.status);
    setQAResultNotes(inspection.result_notes || '');
    setShowQADialog(true);
  };

  const handleSubmitQA = async () => {
    if (!selectedQAInspection) return;
    
    try {
      await axios.post(`${API}/production/${wo_id}/log-qa-inspection`, null, {
        params: {
          inspection_id: selectedQAInspection.inspection_id,
          status: qaStatus,
          result_notes: qaResultNotes,
          inspected_by: 'qa_inspector'
        }
      });
      toast.success('QA inspection logged');
      setShowQADialog(false);
      // Refresh will happen via polling
    } catch (error) {
      toast.error('Failed to log QA inspection');
    }
  };

  const handleStageForProduction = async () => {
    try {
      await axios.post(`${API}/production/${wo_id}/stage-production`);
      toast.success('Work order staged for production');
      // Refresh will happen via polling
    } catch (error) {
      toast.error('Failed to stage work order');
    }
  };

  const handleStartProduction = async () => {
    try {
      await axios.post(`${API}/production/${wo_id}/start`);
      toast.success('Production started');
      // Refresh will happen via polling
    } catch (error) {
      toast.error('Failed to start production');
    }
  };

  const handleCompleteProduction = async () => {
    const confirmed = window.confirm(`Complete production for ${workOrder.wo_number}?`);
    if (confirmed) {
      try {
        await axios.post(`${API}/production/${wo_id}/complete-production`);
        toast.success('Production completed');
        // Refresh will happen via polling
      } catch (error) {
        toast.error('Failed to complete production');
      }
    }
  };

  const handleLogPallet = async () => {
    const cases = window.prompt(`Enter cases per pallet for ${workOrder.item_name}:`, '50');
    if (!cases) return;
    
    const casesNum = parseFloat(cases);
    if (isNaN(casesNum) || casesNum <= 0) {
      toast.error('Please enter a valid number of cases');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/production/${wo_id}/log-pallet`, {
        cases: casesNum,
        logged_by: 'operator'
      });
      
      const data = response.data;
      toast.success(`✅ Pallet #${data.pallets_completed} logged: ${casesNum} cases`);
      
      // Refresh will happen via polling
    } catch (error) {
      toast.error('Failed to log pallet');
    }
  };

  const handleLogDowntime = async () => {
    const reason = window.prompt('Enter downtime reason:');
    if (!reason) return;
    
    const minutes = window.prompt('Enter duration in minutes:');
    if (!minutes) return;
    
    try {
      await axios.post(`${API}/production/${wo_id}/log-downtime`, {
        reason_code: 'manual',
        reason_description: reason,
        duration_minutes: parseFloat(minutes),
        logged_by: 'user'
      });
      toast.success('Downtime logged');
      // Refresh will happen via polling
    } catch (error) {
      toast.error('Failed to log downtime');
    }
  };

  const handleSyncWithNetSuite = async () => {
    const confirmed = window.confirm(`Sync ${workOrder.wo_number} with NetSuite? This will push all current data to NetSuite.`);
    if (confirmed) {
      try {
        toast.info('Syncing with NetSuite...');
        await axios.post(`${API}/production/${wo_id}/sync-netsuite`);
        toast.success('Successfully synced with NetSuite');
        // Refresh will happen via polling
      } catch (error) {
        toast.error('Failed to sync with NetSuite');
        console.error('NetSuite sync error:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-gray-500">Loading work order details...</div>
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="p-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Work Order Not Found</h2>
          <Button onClick={() => navigate(backPath)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {backLabel}
          </Button>
        </div>
      </div>
    );
  }

  // Chart data derived from pallet log and work order configuration
  const upmData = generateUPMData(workOrder, chartTimeRange);
  const hourlyData = generateHourlyData(workOrder, chartTimeRange);
  const maxHourlyValue = hourlyData.length
    ? Math.max(...hourlyData.map(d => Math.max(d.actual || 0, d.target || 0)))
    : 0;
  const hourlyDomainMax = maxHourlyValue ? Math.ceil(maxHourlyValue * 1.1) : 10;

  return (
    <div className="work-order-detail-page p-6 bg-gray-50 min-h-screen">
      {/* Header with Back Button */}
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate(backPath)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {backLabel}
        </Button>
      </div>
      
      {/* Work Order Header Card - Reorganized with Action Buttons */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Work Order Info with Item Image (1 column) */}
            <div className="lg:col-span-1">
              <div className="flex items-start gap-3 mb-4">
                {/* Item Image - Smaller */}
                <div className="w-16 h-16 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0 border-2 border-gray-200 flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_mfg-workorder-app/artifacts/2rmfbvf8_MFGIcon.png"
                    alt={workOrder.item_name}
                    className="w-12 h-12 object-contain"
                    style={{ backgroundColor: 'transparent' }}
                  />
                </div>
                
                {/* WO Info */}
                <div className="flex-1">
                  <h1 className="text-xl font-bold text-gray-900 mb-1">{workOrder.wo_number}</h1>
                  <h2 className="text-sm text-gray-600 mb-1">{workOrder.item_name}</h2>
                  <p className="text-xs text-gray-500 mb-2">Line: {workOrder.production_line}</p>
                  <Badge 
                    className={
                      workOrder.status === 'finalized' ? 'bg-green-100 text-green-800' :
                      workOrder.status === 'production_complete' ? 'bg-blue-100 text-blue-800' :
                      workOrder.status === 'in_production' || workOrder.status === 'in_progress' ? 'bg-purple-100 text-purple-800' :
                      workOrder.status === 'staged' ? 'bg-yellow-100 text-yellow-800' :
                      workOrder.status === 'scheduled' ? 'bg-gray-100 text-gray-800' :
                      'bg-red-100 text-red-800'
                    }
                  >
                    {workOrder.status.replace(/_/g, ' ').toUpperCase()}
                  </Badge>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-y-1 text-xs border-t pt-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Date:</span>
                  <span className="font-semibold">{workOrder.planned_date ? format(new Date(workOrder.planned_date), 'MMM dd') : 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Time:</span>
                  <span className="font-semibold">{workOrder.planned_start_time}-{workOrder.planned_end_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Planned:</span>
                  <span className="font-semibold">{workOrder.planned_quantity} cases</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Actual:</span>
                  <span className="font-semibold text-blue-600">{workOrder.actual_quantity} cases</span>
                </div>
                {workOrder.pallets_completed > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Pallets:</span>
                    <span className="font-semibold text-green-600">{workOrder.pallets_completed}</span>
                  </div>
                )}
                {workOrder.downtime_minutes > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Downtime:</span>
                    <span className="font-semibold text-red-600">{workOrder.downtime_minutes} min</span>
                  </div>
                )}
              </div>
            </div>

            {/* Middle: Charts (2 columns) */}
            <div className="lg:col-span-2">
              {/* Time Range Controls */}
              <div className="flex items-center justify-end gap-2 mb-3">
                <span className="text-xs text-gray-500 mr-2">Time Range:</span>
                <Button
                  size="sm"
                  variant={chartTimeRange === 'today' ? 'default' : 'outline'}
                  className="h-7 text-xs"
                  onClick={() => setChartTimeRange('today')}
                >
                  Today
                </Button>
                <Button
                  size="sm"
                  variant={chartTimeRange === 'last4hours' ? 'default' : 'outline'}
                  className="h-7 text-xs"
                  onClick={() => setChartTimeRange('last4hours')}
                >
                  Last 4 Hours
                </Button>
                <Button
                  size="sm"
                  variant={chartTimeRange === 'last1hour' ? 'default' : 'outline'}
                  className="h-7 text-xs"
                  onClick={() => setChartTimeRange('last1hour')}
                >
                  Last Hour
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* UPM Chart with Multi-Colored Line */}
                <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Units Per Minute (UPM)</h3>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={upmData}>
                  <XAxis 
                    dataKey="time" 
                    tick={{ fontSize: 8 }}
                    interval={chartTimeRange === 'today' ? 59 : chartTimeRange === 'last4hours' ? 29 : 9}
                  />
                  <YAxis 
                    domain={['auto', 'auto']}
                    tick={{ fontSize: 10 }}
                    width={30}
                  />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        const variance = parseFloat(data.variance);
                        return (
                          <div className="bg-white p-2 border rounded shadow-sm text-xs">
                            <p className="font-semibold">{data.time}</p>
                            <p>UPM: <span className="font-bold">{data.upm}</span></p>
                            <p className={variance > 0 ? 'text-green-600' : 'text-red-600'}>
                              {variance > 0 ? '+' : ''}{variance}%
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* Target line */}
                  <Line 
                    type="monotone" 
                    dataKey="target" 
                    stroke="#94a3b8" 
                    strokeDasharray="3 3" 
                    dot={false} 
                    strokeWidth={1}
                  />
                  {/* Actual UPM line */}
                  <Line 
                    type="monotone" 
                    dataKey="upm" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Right: Hourly Production Chart */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Hourly Production</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={hourlyData}>
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, hourlyDomainMax]} tick={{ fontSize: 10 }} width={30} />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        const pct = data.target ? (data.actual / data.target * 100).toFixed(0) : '0';
                        return (
                          <div className="bg-white p-2 border rounded shadow-sm text-xs">
                            <p className="font-semibold">{data.hour}</p>
                            <p>Actual: <span className="font-bold">{data.actual}</span> units</p>
                            <p>Target: {data.target} units</p>
                            <p className={
                              pct >= 110 ? 'text-green-600' : 
                              pct >= 90 ? 'text-blue-600' : 
                              'text-red-600'
                            }>
                              {pct}% of target
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* Target line (dashed) */}
                  <Line 
                    type="monotone" 
                    dataKey="target" 
                    stroke="#94a3b8" 
                    strokeDasharray="5 5"
                    dot={false} 
                    strokeWidth={2}
                  />
                  {/* Single bar with dynamic color */}
                  {hourlyData.map((entry, index) => {
                    const pct = (entry.actual / entry.target) * 100;
                    let barColor = '#3b82f6'; // Blue (90-110%)
                    if (pct >= 110) {
                      barColor = '#22c55e'; // Green (>110%)
                    } else if (pct < 90) {
                      barColor = '#ef4444'; // Red (<90%)
                    }
                    return (
                      <Bar 
                        key={index}
                        dataKey="actual" 
                        data={[entry]}
                        fill={barColor}
                        radius={[4, 4, 0, 0]}
                        opacity={entry.opacity || 1}
                      />
                    );
                  })}
                </BarChart>
              </ResponsiveContainer>
            </div>
              </div>
            </div>

            {/* Pallet Log (1 column) */}
            <div className="lg:col-span-1">
              <PalletLog
                workOrder={workOrder}
                onPalletAdded={() => {
                  fetchWorkOrder?.();
                }}
              />
            </div>

            {/* Right: Action Buttons (1 column) */}
            <div className="lg:col-span-1 border-l pl-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Actions</h3>
              <div className="flex flex-col gap-2">
                {/* Scheduled Status: Show Stage for Production button */}
                {workOrder.status === 'scheduled' && (
                  <Button 
                    size="sm"
                    onClick={handleStageForProduction}
                    className="bg-yellow-600 hover:bg-yellow-700 w-full justify-start"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Stage for Production
                  </Button>
                )}

                {/* Staged Status: Show Start Production button */}
                {workOrder.status === 'staged' && (
                  <Button 
                    size="sm"
                    onClick={handleStartProduction}
                    className="bg-green-600 hover:bg-green-700 w-full justify-start"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start Production
                  </Button>
                )}

                {/* In Production Status: Show action buttons (in_progress = legacy same as in_production) */}
                {(workOrder.status === 'in_production' || workOrder.status === 'in_progress') && (
                  <>
                    <Button 
                      size="sm"
                      onClick={handleLogPallet}
                      variant="outline"
                      className="w-full justify-start"
                    >
                      <Package className="w-4 h-4 mr-2" />
                      Log Pallet
                    </Button>
                    <Button 
                      size="sm"
                      onClick={handleLogDowntime}
                      variant="outline"
                      className="w-full justify-start"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Log Downtime
                    </Button>
                    <Button 
                      size="sm"
                      onClick={handleCompleteProduction}
                      className="bg-green-600 hover:bg-green-700 w-full justify-start"
                    >
                      <CheckSquare className="w-4 h-4 mr-2" />
                      Complete Production
                    </Button>
                  </>
                )}

                {/* Production Complete Status: Show finalize option */}
                {workOrder.status === 'production_complete' && (
                  <>
                    <Button 
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 w-full justify-start"
                    >
                      <CheckSquare className="w-4 h-4 mr-2" />
                      Finalize Work Order
                    </Button>
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={handleSyncWithNetSuite}
                      className="w-full justify-start border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      <Cloud className="w-4 h-4 mr-2" />
                      Sync with NetSuite
                    </Button>
                  </>
                )}

                {/* Finalized Status: Show sync button */}
                {workOrder.status === 'finalized' && (
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={handleSyncWithNetSuite}
                    className="w-full justify-start border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    <Cloud className="w-4 h-4 mr-2" />
                    Sync with NetSuite
                  </Button>
                )}

                {/* Finalized Status: Show message */}
                {workOrder.status === 'finalized' && (
                  <Badge className="bg-green-100 text-green-800 text-xs px-3 py-2 justify-start">
                    <CheckSquare className="w-3 h-3 mr-2" />
                    Finalized
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Main Content - Takes 3 columns */}
        <div className="xl:col-span-3 space-y-6">
          
          {/* AI Camera Feeds - More Prominent */}
          {(workOrder.status === 'in_production' || workOrder.status === 'in_progress') && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <CameraIcon className="w-6 h-6" />
                  Live AI Camera Feeds
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { name: 'Infeed Camera', bg: 'from-blue-900 to-blue-800' },
                    { name: 'Production Line Camera', bg: 'from-purple-900 to-purple-800' },
                    { name: 'Packaging Camera', bg: 'from-green-900 to-green-800' }
                  ].map((camera, idx) => (
                    <div key={idx} className={`aspect-video bg-gradient-to-br ${camera.bg} rounded-lg flex items-center justify-center relative overflow-hidden shadow-lg`}>
                      <div className="absolute inset-0 bg-black opacity-20"></div>
                      <div className="text-center text-white relative z-10">
                        <CameraIcon className="w-12 h-12 mx-auto mb-3 animate-pulse" />
                        <p className="text-sm font-semibold">{camera.name}</p>
                        <p className="text-xs text-gray-300 mt-1">● LIVE</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* QA Inspections Section */}
          {workOrder.qa_inspections && workOrder.qa_inspections.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="w-5 h-5" />
                  QA Inspections
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Daily Inspections */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Daily Inspections</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {workOrder.qa_inspections && workOrder.qa_inspections.filter(i => i.inspection_type === 'daily').map((inspection, idx) => (
                        <div 
                          key={idx} 
                          className="border rounded p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => handleOpenQADialog(inspection)}
                        >
                          <div className="flex items-start justify-between mb-1">
                            <h5 className="font-semibold text-sm">{inspection.inspection_name}</h5>
                            <Badge 
                              variant="outline"
                              className={
                                inspection.status === 'passed' ? 'bg-green-50 text-green-700 border-green-300' :
                                inspection.status === 'failed' ? 'bg-red-50 text-red-700 border-red-300' :
                                'bg-gray-50 text-gray-700 border-gray-300'
                              }
                            >
                              {inspection.status.toUpperCase()}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500 mb-2">{inspection.description}</p>
                          {inspection.result_notes && (
                            <p className="text-xs text-gray-700 italic">"{inspection.result_notes}"</p>
                          )}
                          {inspection.inspected_at && (
                            <p className="text-xs text-gray-400 mt-1">
                              {format(new Date(inspection.inspected_at), 'MMM dd, HH:mm')} • {inspection.inspected_by}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Item-Specific Inspections */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Item-Specific Inspections</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {workOrder.qa_inspections && workOrder.qa_inspections.filter(i => i.inspection_type === 'item_specific').map((inspection, idx) => (
                        <div 
                          key={idx} 
                          className="border rounded p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => handleOpenQADialog(inspection)}
                        >
                          <div className="flex items-start justify-between mb-1">
                            <div>
                              <h5 className="font-semibold text-sm">{inspection.inspection_name}</h5>
                              <p className="text-xs text-gray-500">{inspection.frequency}</p>
                            </div>
                            <Badge 
                              variant="outline"
                              className={
                                inspection.status === 'passed' ? 'bg-green-50 text-green-700 border-green-300' :
                                inspection.status === 'failed' ? 'bg-red-50 text-red-700 border-red-300' :
                                'bg-gray-50 text-gray-700 border-gray-300'
                              }
                            >
                              {inspection.status.toUpperCase()}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500 mb-2">{inspection.description}</p>
                          {inspection.result_notes && (
                            <p className="text-xs text-gray-700 italic">"{inspection.result_notes}"</p>
                          )}
                          {inspection.inspected_at && (
                            <p className="text-xs text-gray-400 mt-1">
                              {format(new Date(inspection.inspected_at), 'MMM dd, HH:mm')} • {inspection.inspected_by}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Charts Section - Keep as is but moved after QA */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Charts removed from here as they're now in header */}
          </div>

          {/* BOM Panel - Smaller with View Pick Log button */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Package className="w-5 h-5" />
                  Bill of Materials
                </CardTitle>
                {(workOrder.bom_usage?.length === 0 || !workOrder.bom_usage?.length) && (
                  <Button size="sm" variant="outline" onClick={handleRefreshBOM}>
                    Refresh BOM from item
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(workOrder.bom_usage || []).length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <p className="text-sm mb-2">No BOM components on this work order.</p>
                    <p className="text-xs mb-3">Add a BOM for this item on the Item detail page (when available), then click &quot;Refresh BOM from item&quot; above.</p>
                    <Button size="sm" variant="outline" onClick={handleRefreshBOM}>
                      Refresh BOM from item
                    </Button>
                  </div>
                ) : (
                (workOrder.bom_usage || []).map((component, idx) => {
                  const actualUsage = calculateActualUsage(component.lots);
                  const theoretical = calculateTheoreticalUsage(
                    component.planned_qty,
                    workOrder.actual_quantity,
                    workOrder.planned_quantity
                  );
                  const yieldPct = calculateYield(actualUsage, theoretical);
                  const yieldColor = getYieldColor(yieldPct);

                  return (
                    <div key={idx} className="border-l-4 border-blue-500 pl-3 py-2 bg-gray-50 rounded-r">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-sm text-gray-900">{component.component_name}</h4>
                        <Badge variant="outline" className="text-xs">{component.item_type}</Badge>
                      </div>

                      <div className="grid grid-cols-4 gap-2 mb-2 text-xs">
                        <div>
                          <p className="text-gray-500">Planned</p>
                          <p className="font-semibold">{component.planned_qty.toFixed(0)} {component.unit}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Actual</p>
                          <p className="font-semibold text-blue-600">{actualUsage.toFixed(1)} {component.unit}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Theoretical</p>
                          <p className="font-semibold">{theoretical.toFixed(1)} {component.unit}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Yield</p>
                          <p className={`font-semibold ${yieldColor}`}>{yieldPct}%</p>
                        </div>
                      </div>

                      {/* Show Pick/Return/Waste for Staged, In Progress, In Production, or Production Complete */}
                      {(workOrder.status === 'staged' || workOrder.status === 'in_progress' || workOrder.status === 'in_production' || workOrder.status === 'production_complete') && (
                        <div className="flex gap-1 flex-wrap">
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-xs h-7"
                            onClick={() => handleOpenPickModal(component)}
                          >
                            <PackagePlus className="w-3 h-3 mr-1" />
                            Pick
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-xs h-7"
                            onClick={() => handleOpenReturnModal(component)}
                          >
                            <RotateCcw className="w-3 h-3 mr-1" />
                            Return
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-xs h-7"
                            onClick={() => handleOpenWasteModal(component)}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Waste
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-xs h-7"
                            onClick={() => handleViewPickLog(component)}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View Log
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar - Activity Log */}
        <div className="xl:col-span-1">
          <Card className="sticky top-6 max-h-[calc(100vh-100px)] overflow-hidden flex flex-col">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Activity className="w-5 h-5" />
                Activity Log
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto">
              <div className="space-y-3">
                {workOrder.activity_log && workOrder.activity_log.length > 0 ? (
                  [...workOrder.activity_log].reverse().map((activity, idx) => (
                    <div key={idx} className="border-l-2 border-blue-500 pl-2 py-1.5 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start gap-2">
                        {activity.activity_type === 'pick' && <PackagePlus className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />}
                        {activity.activity_type === 'return' && <RotateCcw className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />}
                        {activity.activity_type === 'waste' && <Trash2 className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />}
                        {activity.activity_type === 'comment' && <MessageSquare className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />}
                        {activity.activity_type === 'photo' && <ImageIcon className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />}
                        {activity.activity_type === 'pallet_complete' && <Package className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />}
                        {activity.activity_type === 'downtime' && <Clock className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />}
                        {activity.activity_type === 'status_change' && <TrendingUp className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />}
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 leading-tight">{activity.description}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {activity.timestamp ? format(new Date(activity.timestamp), 'HH:mm') : 'N/A'} • {activity.user}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-8">No activity logged yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Comments Section */}
          {workOrder.comments && workOrder.comments.length > 0 && (
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <MessageSquare className="w-4 h-4" />
                  Comments ({workOrder.comments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {workOrder.comments.map((comment, idx) => (
                    <div key={idx} className="bg-gray-50 rounded p-2 text-xs">
                      <p className="text-gray-900">{comment.comment_text}</p>
                      <p className="text-gray-500 mt-1">
                        {comment.created_at ? format(new Date(comment.created_at), 'MMM dd, HH:mm') : 'N/A'} • {comment.created_by}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Photos Section */}
          {workOrder.photos && workOrder.photos.length > 0 && (
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ImageIcon className="w-4 h-4" />
                  Photos ({workOrder.photos.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {workOrder.photos.map((photo, idx) => (
                    <div key={idx} className="border rounded overflow-hidden">
                      <img 
                        src={photo.photo_url} 
                        alt={photo.caption || 'Work order photo'} 
                        className="w-full h-auto"
                      />
                      {photo.caption && <p className="text-xs text-gray-700 p-2">{photo.caption}</p>}
                      <p className="text-xs text-gray-500 px-2 pb-2">
                        {photo.uploaded_at ? format(new Date(photo.uploaded_at), 'MMM dd, HH:mm') : 'N/A'}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Modals */}
      {showPickModal && selectedComponent && (
        <LogPickModal
          isOpen={showPickModal}
          onClose={() => setShowPickModal(false)}
          workOrder={workOrder}
          component={selectedComponent}
          onSuccess={() => {
            setShowPickModal(false);
            // Refresh will happen via polling
          }}
        />
      )}

      {showReturnModal && selectedComponent && (
        <LogReturnModal
          isOpen={showReturnModal}
          onClose={() => setShowReturnModal(false)}
          workOrder={workOrder}
          component={selectedComponent}
          onSuccess={() => {
            setShowReturnModal(false);
          }}
        />
      )}

      {showWasteModal && selectedComponent && (
        <LogWasteModal
          isOpen={showWasteModal}
          onClose={() => setShowWasteModal(false)}
          workOrder={workOrder}
          component={selectedComponent}
          onSuccess={() => {
            setShowWasteModal(false);
          }}
        />
      )}

      {/* Pick Log Modal */}
      {showPickLogModal && pickLogComponent && (
        <PickLogModal
          isOpen={showPickLogModal}
          onClose={() => setShowPickLogModal(false)}
          component={pickLogComponent}
          workOrder={workOrder}
        />
      )}

      {/* QA Inspection Dialog */}
      {showQADialog && selectedQAInspection && (
        <Dialog open={showQADialog} onOpenChange={setShowQADialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{selectedQAInspection.inspection_name}</DialogTitle>
              <p className="text-sm text-gray-500">{selectedQAInspection.description}</p>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="qaStatus">Status</Label>
                <select
                  id="qaStatus"
                  value={qaStatus}
                  onChange={(e) => setQAStatus(e.target.value)}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="pending">Pending</option>
                  <option value="passed">Passed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              
              <div>
                <Label htmlFor="qaResultNotes">Result Notes</Label>
                <textarea
                  id="qaResultNotes"
                  value={qaResultNotes}
                  onChange={(e) => setQAResultNotes(e.target.value)}
                  placeholder="Enter inspection results..."
                  rows={4}
                  className="w-full p-2 border rounded-md"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSubmitQA}>
                Log Inspection
              </Button>
              <Button variant="outline" onClick={() => setShowQADialog(false)}>
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default WorkOrderDetailPage;
